import React, { useEffect, useRef, useState } from 'react';
import {
  Share2,
  Instagram,
  Music2,
  MessageCircle,
  Twitter,
  Facebook,
  Link as LinkIcon,
  Check,
} from 'lucide-react';
import { trackPostEvent } from '../../lib/api';

/**
 * Luxury share menu for Sevagram posts. Embeds the post's unique referral link
 * (?ref=<post_id>) into every external share. Telemetry is logged via
 * /api/sevagram/posts/{id}/track for dashboard analytics.
 */
const ShareMenu = ({ post, siteOrigin }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const origin =
    siteOrigin ||
    (typeof window !== 'undefined'
      ? window.location.origin
      : 'https://shreesavaa.com');
  const refLink = `${origin}/shop?ref=${post.id}`;
  const shareText = `${post.caption} — via @${post.creator_handle} on Sevagram`;
  const utmLink = (channel) =>
    `${refLink}&utm_source=${channel}&utm_medium=social&utm_campaign=sevagram`;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const fire = (channel, url) => {
    trackPostEvent(post.id, 'share', channel);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  const handleCopy = async () => {
    // Fire telemetry first so a clipboard permission error never drops the event.
    trackPostEvent(post.id, 'share', 'copy');
    try {
      await navigator.clipboard.writeText(utmLink('copy'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* noop — telemetry already recorded */
    }
  };

  const handleNative = async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: 'Sevagram', text: shareText, url: refLink });
        trackPostEvent(post.id, 'share', 'copy');
        setOpen(false);
        return true;
      } catch {
        /* fall through to menu */
      }
    }
    return false;
  };

  const onToggle = async () => {
    if (!open) {
      const used = await handleNative();
      if (used) return;
    }
    setOpen((v) => !v);
  };

  const channels = [
    {
      key: 'instagram',
      label: 'Instagram',
      icon: Instagram,
      onClick: () => {
        // IG has no public share-intent URL — copy link, then open IG
        navigator.clipboard?.writeText(utmLink('instagram'));
        fire('instagram', 'https://www.instagram.com');
      },
      hint: 'Link copied — paste into your story or DM',
    },
    {
      key: 'tiktok',
      label: 'TikTok',
      icon: Music2,
      onClick: () => {
        navigator.clipboard?.writeText(utmLink('tiktok'));
        fire('tiktok', 'https://www.tiktok.com/upload');
      },
      hint: 'Link copied — paste into your TikTok caption',
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: MessageCircle,
      onClick: () =>
        fire(
          'whatsapp',
          `https://wa.me/?text=${encodeURIComponent(`${shareText} ${utmLink('whatsapp')}`)}`
        ),
    },
    {
      key: 'twitter',
      label: 'X / Twitter',
      icon: Twitter,
      onClick: () =>
        fire(
          'twitter',
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(utmLink('twitter'))}`
        ),
    },
    {
      key: 'facebook',
      label: 'Facebook',
      icon: Facebook,
      onClick: () =>
        fire(
          'facebook',
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(utmLink('facebook'))}`
        ),
    },
  ];

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={onToggle}
        data-testid={`share-toggle-${post.id}`}
        className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-[#7C9CB1] hover:text-[#90B0BF] transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Share2 size={13} strokeWidth={1.5} />
        Share
      </button>

      {open && (
        <div
          data-testid={`share-menu-${post.id}`}
          role="menu"
          className="absolute right-0 mt-3 w-72 z-30 bg-[#F4F8F9]/95 backdrop-blur-2xl border border-[#C1D7E2] shadow-[0_20px_60px_-15px_rgba(124,156,177,0.45)]"
        >
          <div className="px-5 pt-5 pb-3">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
              Share with your link
            </p>
            <p className="mt-1 font-serif text-lg text-[#7C9CB1] leading-tight">
              Earn $SEVA on every order.
            </p>
          </div>

          <div className="px-2 pb-2">
            {channels.map((c) => (
              <button
                key={c.key}
                onClick={c.onClick}
                role="menuitem"
                data-testid={`share-${c.key}-${post.id}`}
                className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-[#C1D7E2]/40 transition-colors"
              >
                <c.icon size={16} strokeWidth={1.5} className="text-[#7C9CB1]" />
                <div className="min-w-0">
                  <p className="text-sm text-[#7C9CB1]">{c.label}</p>
                  {c.hint && (
                    <p className="font-mono text-[10px] text-[#90B0BF] mt-0.5 truncate">
                      {c.hint}
                    </p>
                  )}
                </div>
              </button>
            ))}

            <button
              onClick={handleCopy}
              role="menuitem"
              data-testid={`share-copy-${post.id}`}
              className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-[#C1D7E2]/40 transition-colors border-t border-[#C1D7E2]"
            >
              {copied ? (
                <Check size={16} strokeWidth={1.5} className="text-[#7C9CB1]" />
              ) : (
                <LinkIcon size={16} strokeWidth={1.5} className="text-[#7C9CB1]" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm text-[#7C9CB1]">
                  {copied ? 'Link copied' : 'Copy referral link'}
                </p>
                <p className="font-mono text-[10px] text-[#90B0BF] mt-0.5 truncate">
                  {refLink}
                </p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareMenu;
